import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  resolveGalleryCssVars,
  resolveGallerySurface,
  resolveGalleryTextInk,
  resolveCardRadius,
  isNeutralTheme,
  DEFAULT_ACCENT,
  GALLERY_CARD_BACKGROUND,
} from "./galleryTheme";
import { T } from "./constants";
import { UserActionsView } from "../UserActions";
import { autoTextColor } from "../../utils/contrast";
import { colors } from "../../utils";
import { radius } from "../../theme/tokens";
import SearchField from "../WheelGallery/WheelListing/ListComponents/SearchField";
import {
  CollectionsCarouselRow,
  TabStripRow,
  TabStripScrollWrap,
  TabStrip,
  CollectionTab,
  CollectionTabCount,
} from "../WheelGallery/WheelListing/FoldersV2/styles";
import {
  WHEEL_COLLECTIONS,
  DEFAULT_COLLECTION,
} from "../WheelGallery/WheelListing/FoldersV2/wheelCollections";
import { COLLECTION_CONTROL_LABELS } from "../WheelGallery/WheelListing/FoldersV2/collectionsCopy";
// Gordon 2026-09-16 (Themes item 3): the SAME shared tile the real Gallery
// Collections row renders — the preview can no longer drift from it.
import CollectionCard from "../WheelGallery/WheelListing/FoldersV2/CollectionCard";

// Mirrors CollectionTabs.jsx's own WHEEL_SOURCE_TABS_LABEL constant. Not
// imported directly: that file also imports useAllTabCounts (a live
// react-query hook hitting /api/v2/wheel/), which this design-only preview
// must never depend on (same reasoning as UserActionsView below).
const WHEEL_SOURCE_TABS_LABEL = "Filter wheels by source";

// A tiny Segment-donut standing in for a real Wheel card without pulling the
// heavy D3 renderer. A Gallery Theme styles the page chrome only (wallpaper,
// accent, buttons, link border) and must never touch Wheel segment art, so
// this ring is a fixed cheerful palette independent of the live theme accent
// (ISP-9999, gordon-0913-gallery-theme-preset-recolors-wheel-segments — the
// ring used to splice `accent` in at index 1, so picking a preset visibly
// recolored the bottom-right "segment" of every preview thumbnail).
const Donut = ({ label }) => {
  const ring = ["#F79A00", "#9B5AF8", "#29C18C", "#E25297"];
  const seg = (i) => {
    const a0 = (i / 4) * 2 * Math.PI - Math.PI / 2;
    const a1 = ((i + 1) / 4) * 2 * Math.PI - Math.PI / 2;
    const r = 42;
    const cx = 50;
    const cy = 50;
    const x0 = cx + r * Math.cos(a0);
    const y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1);
    const y1 = cy + r * Math.sin(a1);
    return `M${cx} ${cy} L${x0} ${y0} A${r} ${r} 0 0 1 ${x1} ${y1} Z`;
  };
  return (
    <svg viewBox="0 0 100 100" width="72" height="72" aria-hidden="true">
      {ring.map((c, i) => (
        <path key={i} data-testid={`gt-preview-seg-${label}-${i}`} d={seg(i)} fill={c} />
      ))}
      <circle cx="50" cy="50" r="20" fill="#EDE0FD" />
      <text
        x="50"
        y="55"
        textAnchor="middle"
        fontSize="14"
        fontWeight="700"
        fill="#6f27d6"
        fontFamily="DM Sans, sans-serif"
      >
        {label}
      </text>
    </svg>
  );
};

const CARDS = [
  { label: "GA", title: "Morning Routine", primary: true },
  { label: "SP", title: "Studio Playlist" },
  { label: "FR", title: "Fitness Reset" },
  { label: "RL", title: "Reading List" },
];

// Sample Collections strip content (FoldersV2/CollectionsSurface renders the
// owner's real folders here; this is a static stand-in, same rationale as
// CARDS above).
const DEMO_SEGMENT_COLORS = ["#F79A00", "#9B5AF8", "#29C18C", "#E25297"];
const demoCoverWheel = (id) => ({
  id,
  wheel_segments: DEMO_SEGMENT_COLORS.map((color, position) => ({
    id: `${id}-${position}`,
    position,
    color,
  })),
});
const DEMO_COLLECTIONS = [
  { id: "favorites", title: "Favorites", count: 5 },
  { id: "summer", title: "Summer Wheels", count: 3 },
].map((c) => ({
  ...c,
  cover: { wheelId: `preview-${c.id}`, wheel: demoCoverWheel(`preview-${c.id}`) },
}));

// Sample per-tab counts for the Created/Cloned/Unlocked/Favorited strip —
// stands in for the real live useAllTabCounts hook (see WHEEL_SOURCE_TABS_LABEL
// comment above for why that hook is never mounted here).
const DEMO_TAB_COUNTS = { created: 9, cloned: 2, unlocked: 1, favorited: 0 };

// WEB-E2E-2 — mirrors the REAL profile-card element order/labels rendered by
// components/WheelGallery/Profile/index.jsx (name+username / avatar / Follow
// button row -> Wheels|Followers|Following stats row -> bio -> LinkVault/
// share row), instead of an invented avatar+bio+Follow-Share+4-tab layout that
// never existed on the live Gallery. Representative sample counts only (a
// live-data preview would need the real profile fetch, out of scope here).
const PREVIEW_STATS = { wheels: 12, followers: 4200, following: 86 };

// Live preview of the themed public Gallery. Pure presentational — takes a theme
// object, applies GUARDED css vars, and renders a faithful mini gallery.
const GalleryThemePreview = ({ theme, vaultTheme, displayName = "Gordon Ashworth", handle = "gordon" }) => {
  const vars = useMemo(
    () => resolveGalleryCssVars(theme, vaultTheme),
    [theme, vaultTheme]
  );
  const surface = resolveGallerySurface(theme) || null;
  const accent = vars["--gt-accent"] || DEFAULT_ACCENT;
  // gordon-0913-gallery-theme-auto-contrast-and-follow-icons (A), narrowed by
  // gordon-0913-vy-theme-preview-profile-card-parity: `ink` is the auto-
  // contrast text color for the THEMED WHEELS PANEL surface only
  // (gt-preview-content-panel / CollectionTab below) — it must NOT be used
  // for anything painted on the profile card, which decision
  // #gallery-card-white pins to a fixed white regardless of theme (see the
  // profile-card Typography/UserActionsView usages further down, which now
  // use the same fixed T.ink/T.muted colors the live white card uses).
  const ink = isNeutralTheme(theme) ? null : resolveGalleryTextInk(theme);
  // Accent-branded elements INSIDE the profile card (LinkVault border/text,
  // Follow's "following" tint): the real accent when it already clears AA
  // against GALLERY_CARD_BACKGROUND (the card's actual, always-white
  // background — see resolveGalleryCssVars), else an auto dark/light ink.
  const accentInkBorder = vars["--gt-accent-ink"] || accent;
  const accentInkText = vars["--gt-accent-ink-text"] || accent;
  // gordon-0913-gallery-theme-panel-background-and-accent-card-border (b):
  // the wheel-grid cards' radius must mirror the REAL WheelCard frame's exact
  // gating (pages/Gallery/index.jsx only emits --gt-card-radius when
  // galleryTheme.card_radius is explicitly set; CardContainer's own literal
  // fallback is 8px/radius.md) — NOT `vars["--gt-card-radius"]` above, which
  // resolveGalleryCssVars (used for the profile card) always emits at a
  // default of DEFAULT_CARD_RADIUS (12px) even when unset, a different
  // default than the real wheel card's 8px.
  const wheelCardRadius =
    theme && theme.card_radius != null
      ? `${resolveCardRadius(theme)}px`
      : `${radius.md}px`;

  const initials = displayName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderLeft: `1px solid ${T.line}`,
        minWidth: 0,
      }}
    >
      {/* preview status strip */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: "18px",
          py: "10px",
          borderBottom: `1px solid ${T.line}`,
          background: "#fff",
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.muted, fontFamily: T.fontBody }}>
          Live preview · iyuga.com/gallery/{handle}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: "#22C55E" }} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: accent, fontFamily: T.fontBody }}>
            Updating live
          </Typography>
        </Box>
      </Box>

      {/* themed surface */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          position: "relative",
          // wallpaper as the page background (guarded value or neutral #E8E8E8)
          background: vars["--gt-bg"] || "#E8E8E8",
          p: "18px",
        }}
        style={{ opacity: 1 }}
      >
        {/* top row — mirrors the real Gallery's `profile-primary-wheel-row`
            Stack (WheelListing/index.js): the profile card side by side with
            the primary-wheel banner. */}
        <Box
          data-testid="gt-preview-header-row"
          sx={{ display: "flex", flexWrap: "wrap", gap: "14px" }}
        >
          {/* profile card — element order/labels mirror the real
              WheelGallery/Profile card: name+username/avatar/Follow row, then a
              Wheels|Followers|Following stats row, then bio, then a
              LinkVault/share row (WEB-E2E-2). */}
          <Box
            data-testid="gt-preview-profile-card"
            sx={{
              position: "relative",
              // gordon-0913-vy-theme-preview-profile-card-parity: decision
              // #gallery-card-white (WheelListing/styles.js / Profile/style.js's
              // CardContainer) pins the REAL profile card frame to pure white
              // ALWAYS, regardless of the saved theme -- only the wheels
              // content panel below (gt-preview-content-panel) takes the
              // theme's Background colour. This card used to paint `surface`
              // here, so a themed Background visibly recolored the preview's
              // profile card while the live gallery's stayed white.
              // Gordon 2026-09-16 (Themes item 1) supersedes #gallery-card-white:
              // the card follows the theme Background, same as the live
              // CardContainer (var(--gt-surface, #FFFFFF)). Unset stays white.
              background: surface || GALLERY_CARD_BACKGROUND,
              color: ink || undefined,
              borderRadius: vars["--gt-card-radius"] || "12px",
              p: "22px",
              display: "flex",
              flexDirection: "column",
              gap: "14px",
              boxShadow: "0 1px 3px rgba(18,48,81,0.08)",
              flex: "1 1 260px",
              minWidth: 0,
            }}
          >
          {/* name/username + avatar + Follow */}
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "14px", minWidth: 0 }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  background: accent,
                  // (A): initials text sits ON the accent fill itself (same
                  // "text on its own background" shape as the blocks-law
                  // autoTextColor calls) — a light/near-white accent used to
                  // always render invisible white-on-white text.
                  color: autoTextColor(accent, { fallback: "#fff" }),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 22,
                  fontWeight: 700,
                  fontFamily: T.fontBody,
                  flexShrink: 0,
                  border: "3px solid #fff",
                }}
              >
                {initials}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: ink || T.muted, fontFamily: T.fontBody, textTransform: "capitalize" }}>
                  @{handle}
                </Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: ink || T.ink, fontFamily: T.fontBody, lineHeight: 1.2 }}>
                  {displayName}
                </Typography>
              </Box>
            </Box>
            {/* gordon-0913-gallery-theme-auto-contrast-and-follow-icons (B):
                this used to be a hand-drawn filled "Follow" TEXT pill —
                nothing like the real gallery's icon-only Follow/Message
                controls. Rendering UserActionsView — the exact presentational
                component the live gallery header renders (see Profile/index.jsx)
                — with a fixed not-following demo state means the preview and
                the live gallery header can never visually drift apart again.
                UserActionsView has no data hooks (unlike the default
                UserActions export), so this design-preview surface never
                needs a real userId/redux/react-query context.
                gordon-0913-vy-theme-preview-profile-card-parity: iconColor no
                longer reads the themed `ink` — decision #gallery-card-white
                pins this card to white always (same as the live page), so the
                not-following icon stays UserActionsView's own default color,
                matching Profile/index.jsx's live <UserActions> call (which
                never passes iconColor either). followingIconColor still
                carries the accent-branded auto-contrast ink (A), now resolved
                against that same fixed white card background. */}
            <UserActionsView
              isFollowing={false}
              canMessage
              followingIconColor={accentInkBorder}
            />
          </Box>

          {/* stats row — Wheels | Followers | Following */}
          <Box sx={{ display: "flex", gap: "20px", alignItems: "center" }}>
            {[
              { n: PREVIEW_STATS.wheels, label: "Wheels" },
              { n: "4.2k", label: "Followers" },
              { n: PREVIEW_STATS.following, label: "Following" },
            ].map((s, i) => (
              <Box key={s.label} sx={{ display: "flex", flexDirection: "column", pr: i < 2 ? "18px" : 0, borderRight: i < 2 ? "1px solid #E5E7EB" : "none" }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: ink || T.ink, fontFamily: T.fontBody }}>{s.n}</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: ink || T.muted, fontFamily: T.fontBody }}>{s.label}</Typography>
              </Box>
            ))}
          </Box>

          {/* bio */}
          <Typography sx={{ fontSize: 13, color: ink || "#4a4a4a", fontFamily: T.fontBody }}>
            Building luminous little Wheels. Clone anything you like — tag me and
            I'll re-Clone the good ones back.
          </Typography>

          {/* LinkVault / share row.
              gordon-0913-gallery-theme-auto-contrast-and-follow-icons (A):
              border/text used to paint the literal accent with no contrast
              check against this card's own background — Accent == Background
              (or anything under AA) rendered an invisible border/label.
              accentInkBorder/accentInkText fall back to the auto-contrast ink
              exactly when the accent itself would fail. */}
          <Box
            data-testid="gt-preview-linkvault"
            sx={{ border: `1.5px solid ${accentInkBorder}`, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", py: "8px", fontSize: 13, fontWeight: 600, color: accentInkText, fontFamily: T.fontBody }}
          >
            LinkVault
          </Box>
        </Box>

        {/* primary-wheel banner — sits beside the profile card, matching the
            real row. Corner radius is FIXED at radius.md, unlike the profile
            card above: the real PrimaryWheel wrapper Box
            (WheelListing/index.js) hardcodes `${radius.md}px` and never
            reads --gt-card-radius, so a Card Corners theme change moves the
            profile card's corners but not this one — the preview shows that
            same asymmetry rather than inventing parity. */}
        <Box
          data-testid="gt-preview-primary-wheel"
          sx={{
            flex: "1 1 200px",
            minWidth: 0,
            background: "#fff",
            border: "1px solid #BBBBBB",
            borderRadius: `${radius.md}px`,
            p: "14px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <Box sx={{ background: "#F4F0FE", borderRadius: "50%", p: "6px", flexShrink: 0 }}>
            <Donut label="PW" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.muted, fontFamily: T.fontBody, textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Primary Wheel
            </Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.ink, fontFamily: T.fontBody }}>
              Morning Routine
            </Typography>
          </Box>
        </Box>
        </Box>

        {/* white content panel — mirrors WheelListing/TabComponents/Tabs.jsx's
            "wheels-tabs-wrapper" Stack: 1px colors.NeutralBorder,
            borderRadius 8px. Was NEVER themed as of gordon-0913-gallery-
            theme-preview-layout-must-match-real-gallery (Tabs.jsx read
            neither --gt-surface nor --gt-ink at that point). Superseded here
            by gordon-0913-gallery-theme-panel-background-and-accent-card-
            border (a) — Gordon's explicit follow-up rule: the panel now DOES
            take the theme's Background colour, same as Tabs.jsx, with its
            own auto-contrast ink; unset stays plain white, byte-identical to
            the parity unit's baseline. */}
        <Box
          data-testid="gt-preview-content-panel"
          sx={{
            mt: "16px",
            background: surface || colors.white,
            color: ink || undefined,
            border: `1px solid ${colors.NeutralBorder}`,
            // Gordon 2026-09-16 (Themes item 2): follows Corner Rounding like
            // the live Tabs.jsx backdrop (var(--gt-card-radius, 8px)).
            borderRadius: wheelCardRadius,
            p: "14px",
          }}
          // CollectionTab/CollectionTabCount (FoldersV2/styles.js) are the
          // SAME shared styled components the real Tabs.jsx panel renders —
          // they resolve their own ink from an ancestor's --gt-ink CSS var
          // (the real page sets it on the outer themed Grid), so this panel
          // sets the identical var for its subtree instead of duplicating
          // their colour logic here.
          style={{ "--gt-ink": ink || undefined }}
        >
          {/* search box — same presentational component the real Created tab
              renders (ListComponents/SearchField), narrower for the preview. */}
          <Box sx={{ maxWidth: 220, mb: "12px" }}>
            <SearchField
              value=""
              height={32}
              placeholder={COLLECTION_CONTROL_LABELS.searchPlaceholder}
              ariaLabel={COLLECTION_CONTROL_LABELS.searchAriaLabel}
            />
          </Box>

          {/* Collections strip — sits ABOVE the source tabs, same order as
              FoldersV2/OwnerView (renderCollectionsRow before CollectionTabs). */}
          <CollectionsCarouselRow
            data-testid="gt-preview-collections-strip"
            sx={{ mb: "14px" }}
          >
            {DEMO_COLLECTIONS.map((c) => (
              <CollectionCard
                key={c.id}
                collection={{ id: c.id, title: c.title }}
                cover={c.cover}
              />
            ))}
          </CollectionsCarouselRow>

          {/* Created | Cloned | Unlocked | Favorited source tabs. */}
          <TabStripRow>
            <TabStripScrollWrap>
              <TabStrip role="tablist" aria-label={WHEEL_SOURCE_TABS_LABEL}>
                {WHEEL_COLLECTIONS.map((collection) => {
                  const isSelected = collection.id === DEFAULT_COLLECTION;
                  const Icon = collection.Icon;
                  return (
                    <CollectionTab
                      key={collection.id}
                      type="button"
                      role="tab"
                      aria-selected={isSelected}
                      selected={isSelected}
                    >
                      <span aria-hidden="true" style={{ display: "inline-flex", alignItems: "center" }}>
                        {/* (a): matches CollectionTab's own ink var
                            (FoldersV2/styles.js) — this icon sits directly
                            on the themed panel surface. */}
                        <Icon
                          width={16}
                          height={16}
                          fill={isSelected ? (ink || colors.PrimaryPurple700) : (ink || colors.darkGrey)}
                        />
                      </span>
                      <span>{collection.label}</span>
                      <CollectionTabCount selected={isSelected}>
                        {DEMO_TAB_COUNTS[collection.countKey] ?? 0}
                      </CollectionTabCount>
                    </CollectionTab>
                  );
                })}
              </TabStrip>
            </TabStripScrollWrap>
          </TabStripRow>

          {/* wheel card grid — INSIDE the panel, matching the real Created
              tab (the grid renders inside FoldersV2/OwnerView, itself inside
              the SAME white wrapper Tabs.jsx renders). Corner radius now
              follows --gt-card-radius (see below): gordon-0913-gallery-
              theme-panel-background-and-accent-card-border (b) wires the
              real card frame (WheelListing/styles.js's CardContainer) to
              that var too, so this preview mirrors it for fidelity instead
              of the fixed radius.md this grid previously used. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
              gap: "14px",
            }}
          >
            {CARDS.map((c) => (
              <Box
                key={c.title}
                data-testid={`gt-preview-wheelcard-${c.label}`}
                sx={{
                  background: "#fff",
                  // (b): every wheel card gets a 1px solid Accent border,
                  // universal (default theme included) — `accent` above already
                  // falls back to DEFAULT_ACCENT, mirroring the live Gallery's
                  // always-emitted --gt-card-border (pages/Gallery/index.jsx).
                  border: `1px solid ${accent}`,
                  borderRadius: wheelCardRadius,
                  p: "16px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "10px",
                  boxShadow: "0 1px 3px rgba(18,48,81,0.1)",
                }}
              >
                <Box sx={{ background: "#F4F0FE", borderRadius: wheelCardRadius, p: "10px" }}>
                  <Donut label={c.label} />
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.ink, fontFamily: T.fontBody, textAlign: "center" }}>
                  {c.title}
                </Typography>
                <Typography sx={{ fontSize: 12, color: T.muted, fontFamily: T.fontBody }}>by You</Typography>
                {c.primary && (
                  <Box sx={{ background: "#EDE0FD", color: "#6f27d6", borderRadius: "999px", px: "10px", py: "2px", fontSize: 11, fontWeight: 700, fontFamily: T.fontBody }}>
                    Primary
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default GalleryThemePreview;
